import React, { useMemo } from 'react';
import {
  SCHEMA_GRAZZINI_AXES,
  SCHEMA_GRAZZINI_AREAS,
} from '../../constants/schemaGrazzini';

export const SCHEMA_GRAZZINI_CHART_COLORS = {
  yellow: '#ffcc00',
  green: '#3eb297',
  blue: '#36a9e1',
  pink: '#e95995',
  purple: '#3b235a',
  red: '#e73341',
};

const MAX_VALUE = 5;
const START_ANGLE = -Math.PI / 2;
const TWO_PI = Math.PI * 2;

const SIZE = 720;
const PAD = 56;
const CX = SIZE / 2;
const CY = SIZE / 2;
const RADIUS = 248;
const LABEL_RADIUS = RADIUS + 14;
const AREA_TITLE_RADIUS = RADIUS + 58;

const AREA_STYLES = {
  motoria: { color: '#36a9e1', startIndex: 0, endIndex: 2 },
  relazionale: { color: '#e95995', startIndex: 3, endIndex: 6 },
  cognitiva: { color: '#3eb297', startIndex: 7, endIndex: 12 },
};

const VIEW_MIN = -PAD;
const VIEW_SIZE = SIZE + PAD * 2;

const AREA_ORDER = ['motoria', 'relazionale', 'cognitiva'];

function polar(cx, cy, angle, r) {
  return {
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  };
}

function sectorPath(cx, cy, radius, angleStep, startIndex, endIndex) {
  const a0 = START_ANGLE + (startIndex - 0.5) * angleStep;
  const a1 = START_ANGLE + (endIndex + 0.5) * angleStep;
  const p1 = polar(cx, cy, a0, radius);
  const p2 = polar(cx, cy, a1, radius);
  const largeArc = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${radius} ${radius} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
}

/** Spicchio singolo asse: dal centro fino al raggio proporzionale al valore (0–5). */
function axisValueWedgePath(cx, cy, radius, angleStep, index) {
  const a0 = START_ANGLE + (index - 0.5) * angleStep;
  const a1 = START_ANGLE + (index + 0.5) * angleStep;
  const p1 = polar(cx, cy, a0, radius);
  const p2 = polar(cx, cy, a1, radius);
  return `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${radius} ${radius} 0 0 1 ${p2.x} ${p2.y} Z`;
}

function fillOpacityForValue(value) {
  return 0.22 + 0.58 * (value / MAX_VALUE);
}

function axisAngle(index, angleStep) {
  return START_ANGLE + index * angleStep;
}

function clampValue(raw) {
  const v = Number(raw);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(MAX_VALUE, v));
}

function textAnchorForX(x) {
  if (x > CX + 24) return 'start';
  if (x < CX - 24) return 'end';
  return 'middle';
}

function splitLabel(label) {
  return String(label || '')
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function areaTitleLines(areaKey) {
  if (areaKey === 'cognitiva') {
    return ['AREA COGNITIVO-', 'ESPRESSIVA'];
  }
  return [SCHEMA_GRAZZINI_AREAS[areaKey] || ''];
}

const CSS = `
.grazzini-root { --g-purple: #3b235a; font-family: system-ui, sans-serif; color: var(--g-purple); max-width: 100%; }
.grazzini-layout { display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-start; }
.grazzini-svg-wrap { flex: 1 1 520px; min-width: 280px; max-width: min(100%, 840px); }
.grazzini-svg { display: block; width: 100%; height: auto; overflow: visible; }
.grazzini-side { flex: 1 1 220px; min-width: 200px; max-width: 340px; font-size: 13px; line-height: 1.45; }
.grazzini-side__title { margin: 0 0 0.5rem; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.grazzini-legend { display: flex; align-items: center; gap: 0.5rem; margin: 0.35rem 0; }
.grazzini-swatch { width: 14px; height: 14px; border-radius: 2px; flex-shrink: 0; }
.grazzini-area-block { margin-top: 1rem; }
.grazzini-area-block__head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem; font-weight: 700; font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; }
.grazzini-dim { display: flex; justify-content: space-between; gap: 0.5rem; padding: 0.25rem 0; border-bottom: 1px solid rgba(59,35,90,0.1); font-size: 12px; }
.grazzini-dim strong { font-variant-numeric: tabular-nums; }
.grazzini-grid { fill: none; stroke: var(--g-purple); stroke-opacity: 0.18; stroke-width: 1; }
.grazzini-sector { stroke-width: 1; }
.grazzini-value-wedge { stroke-width: 0; }
.grazzini-scale { font-size: 11px; fill: var(--g-purple); fill-opacity: 0.55; font-weight: 500; }
.grazzini-axis-label { font-size: 11px; fill: var(--g-purple); font-weight: 600; }
.grazzini-area-title { font-size: 14px; font-weight: 800; letter-spacing: 0.06em; }
.grazzini-root--compact .grazzini-axis-label { font-size: 9.5px; }
.grazzini-root--compact .grazzini-area-title { font-size: 11px; }
.grazzini-root--compact .grazzini-scale { font-size: 10px; }
`;

function LegendRow({ areaKey }) {
  const { color } = AREA_STYLES[areaKey];
  const label = SCHEMA_GRAZZINI_AREAS[areaKey].replace(/^AREA /, 'Area ');
  return (
    <div className="grazzini-legend">
      <span
        className="grazzini-swatch"
        style={{ background: color }}
        aria-hidden
      />
      <span>{label}</span>
    </div>
  );
}

function AreaDimensionBlock({ areaKey, axes, values }) {
  const { color } = AREA_STYLES[areaKey];
  const items = axes.filter((a) => a.area === areaKey);
  const head = SCHEMA_GRAZZINI_AREAS[areaKey].replace(/^AREA /, '');
  return (
    <div className="grazzini-area-block">
      <div className="grazzini-area-block__head" style={{ color }}>
        <span
          className="grazzini-swatch"
          style={{ background: color }}
          aria-hidden
        />
        <span>{head}</span>
      </div>
      {items.map((axis) => (
        <div key={axis.key} className="grazzini-dim">
          <span>{splitLabel(axis.label).join(' · ')}</span>
          <strong>{values[axis.key] ?? 0}</strong>
        </div>
      ))}
    </div>
  );
}

function SidePanel({ axes, values, dimensionList }) {
  return (
    <aside className="grazzini-side">
      <p className="grazzini-side__title">Legenda aree</p>
      {AREA_ORDER.map((areaKey) => (
        <LegendRow key={areaKey} areaKey={areaKey} />
      ))}
      {dimensionList
        ? AREA_ORDER.map((areaKey) => (
            <AreaDimensionBlock
              key={areaKey}
              areaKey={areaKey}
              axes={axes}
              values={values}
            />
          ))
        : null}
    </aside>
  );
}

function GrazziniSvg({ axes, angleStep, values, compact }) {
  const labelExtra = compact ? 6 : 10;

  const sectors = useMemo(
    () =>
      AREA_ORDER.map((key) => {
        const cfg = AREA_STYLES[key];
        return {
          key,
          d: sectorPath(
            CX,
            CY,
            RADIUS,
            angleStep,
            cfg.startIndex,
            cfg.endIndex
          ),
          color: cfg.color,
        };
      }),
    [angleStep]
  );

  const rings = useMemo(
    () => [1, 2, 3, 4, 5].map((lv) => (RADIUS * lv) / MAX_VALUE),
    []
  );

  const valueWedges = useMemo(
    () =>
      axes
        .map((axis, i) => {
          const val = clampValue(values[axis.key]);
          if (val <= 0) return null;
          const r = (RADIUS * val) / MAX_VALUE;
          return {
            key: axis.key,
            d: axisValueWedgePath(CX, CY, r, angleStep, i),
            color: AREA_STYLES[axis.area].color,
            fillOpacity: fillOpacityForValue(val),
          };
        })
        .filter(Boolean),
    [axes, angleStep, values]
  );

  const axisLabels = useMemo(
    () =>
      axes.map((axis, i) => {
        const ang = axisAngle(i, angleStep);
        const cos = Math.cos(ang);
        const sin = Math.sin(ang);
        const base = polar(CX, CY, ang, LABEL_RADIUS);
        const x = base.x + cos * labelExtra;
        const y = base.y + sin * labelExtra;
        const lines = splitLabel(axis.label);
        const lineH = (compact ? 10 : 11) * 1.15;
        const y0 = y - ((lines.length - 1) * lineH) / 2;
        return {
          key: axis.key,
          x,
          y: y0,
          anchor: textAnchorForX(x),
          lines,
          lineH,
        };
      }),
    [axes, angleStep, compact, labelExtra]
  );

  const areaTitles = useMemo(
    () =>
      AREA_ORDER.map((areaKey) => {
        const cfg = AREA_STYLES[areaKey];
        const mid =
          START_ANGLE + ((cfg.startIndex + cfg.endIndex) / 2) * angleStep;
        const p = polar(CX, CY, mid, AREA_TITLE_RADIUS);
        const lines = areaTitleLines(areaKey);
        const lineH = compact ? 13 : 16;
        const y0 = p.y - ((lines.length - 1) * lineH) / 2;
        return {
          key: areaKey,
          x: p.x,
          y: y0,
          color: cfg.color,
          anchor: textAnchorForX(p.x),
          lines,
          lineH,
        };
      }),
    [angleStep, compact]
  );

  return (
    <svg
      className="grazzini-svg"
      viewBox={`${VIEW_MIN} ${VIEW_MIN} ${VIEW_SIZE} ${VIEW_SIZE}`}
      role="img"
      aria-label="Schema Grazzini-Hoffmann e Staccioli: ruota pedagogica con spicchi colorati in base ai valori delle tredici dimensioni."
    >
      <title>Schema Grazzini-Hoffmann e Staccioli</title>

      {sectors.map((s) => (
        <path
          key={`zone-${s.key}`}
          d={s.d}
          className="grazzini-sector"
          fill={s.color}
          fillOpacity={0.06}
          stroke={s.color}
          strokeOpacity={0.28}
        />
      ))}

      {valueWedges.map((w) => (
        <path
          key={w.key}
          d={w.d}
          className="grazzini-value-wedge"
          fill={w.color}
          fillOpacity={w.fillOpacity}
        />
      ))}

      {rings.map((r) => (
        <circle
          key={`ring-${r}`}
          cx={CX}
          cy={CY}
          r={r}
          className="grazzini-grid"
        />
      ))}

      {[1, 2, 3, 4, 5].map((level) => (
        <text
          key={`scale-${level}`}
          x={CX + 10}
          y={CY - (RADIUS * level) / MAX_VALUE + 4}
          className="grazzini-scale"
          textAnchor="start"
          dominantBaseline="middle"
        >
          {level}
        </text>
      ))}

      {axisLabels.map((lb) => (
        <text
          key={lb.key}
          x={lb.x}
          y={lb.y}
          textAnchor={lb.anchor}
          dominantBaseline="middle"
          className="grazzini-axis-label"
        >
          {lb.lines.map((line, li) => (
            <tspan
              key={`${lb.key}-${line}`}
              x={lb.x}
              dy={li === 0 ? 0 : lb.lineH}
            >
              {line}
            </tspan>
          ))}
        </text>
      ))}

      {areaTitles.map((t) => (
        <text
          key={t.key}
          x={t.x}
          y={t.y}
          textAnchor={t.anchor}
          dominantBaseline="middle"
          fill={t.color}
          className="grazzini-area-title"
        >
          {t.lines.map((line, li) => (
            <tspan key={`${t.key}-${line}`} x={t.x} dy={li === 0 ? 0 : t.lineH}>
              {line}
            </tspan>
          ))}
        </text>
      ))}
    </svg>
  );
}

/**
 * Ruota pedagogica Grazzini-Hoffmann e Staccioli (SVG + legenda).
 * @param {{ values?: Record<string, number>, compact?: boolean, sidePanel?: boolean, dimensionList?: boolean }} props
 */
function SchemaGrazziniChart({
  values = {},
  compact = false,
  sidePanel = true,
  dimensionList = true,
}) {
  const axes = SCHEMA_GRAZZINI_AXES;
  const angleStep = TWO_PI / axes.length;
  const rootClass = `grazzini-root${compact ? ' grazzini-root--compact' : ''}`;

  return (
    <div className={rootClass}>
      <style>{CSS}</style>
      <div className="grazzini-layout">
        <div className="grazzini-svg-wrap">
          <GrazziniSvg
            axes={axes}
            angleStep={angleStep}
            values={values}
            compact={compact}
          />
        </div>
        {sidePanel ? (
          <SidePanel
            axes={axes}
            values={values}
            dimensionList={dimensionList}
          />
        ) : null}
      </div>
    </div>
  );
}

export default SchemaGrazziniChart;
